import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { EditableImage } from "@/components/EditableImage";
import { AdminToggle } from "@/components/AdminToggle";
import { useExcursions } from "@/hooks/useExcursions";
import { Badge } from "@/components/ui/badge";
import { Clock, MapPin, Users, Star, ArrowLeft, ImageIcon, Plus, Sparkles, Award } from "lucide-react";
import { DifficultyBadge } from "@/components/DifficultyBadge";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import type { Excursion } from "@/hooks/useExcursions";
import { BookingButton } from "@/components/BookingButton";
import { useAdmin } from "@/hooks/useAdmin";
import { useToast } from "@/hooks/use-toast";
import { ExcursionPhotoCarousel } from "@/components/ExcursionPhotoCarousel";
import { ExcursionLightbox } from "@/components/ExcursionLightbox";
import { ExcursionDetailSkeleton } from "@/components/ExcursionDetailSkeleton";
import { ExcursionItinerary } from "@/components/ExcursionItinerary";
import { ExcursionInclusions } from "@/components/ExcursionInclusions";
import { ExcursionPracticalInfoEditor } from "@/components/ExcursionPracticalInfoEditor";
import { ExcursionFAQ } from "@/components/ExcursionFAQ";
import { SecureContentRenderer } from "@/components/SecureContentRenderer";
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage } from "@/components/ui/breadcrumb";
import { SeoHead } from "@/components/SeoHead";
import { TouristAttractionStructuredData, BreadcrumbStructuredData, FAQStructuredData } from "@/components/StructuredData";
import { Helmet } from "react-helmet-async";
import { useAnalytics } from "@/hooks/useAnalytics";

const ExcursionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getExcursionById } = useExcursions();
  const [excursion, setExcursion] = useState<Excursion | null>(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const { isAdmin } = useAdmin();
  const { toast } = useToast();
  const { trackViewExcursion } = useAnalytics();

  useEffect(() => {
    const fetchExcursion = async () => {
      if (id) {
        const data = await getExcursionById(id);
        setExcursion(data);
        
        // Track excursion view
        if (data) {
          trackViewExcursion(data.title, id);
        }
      }
      setLoading(false);
    };
    fetchExcursion();
  }, [id, getExcursionById, trackViewExcursion]);

  useEffect(() => {
    let ticking = false;
    const threshold = window.innerHeight * 0.5;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setShowStickyCTA(window.scrollY > threshold);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <main>
          <ExcursionDetailSkeleton />
        </main>
        <Footer />
      </div>
    );
  }

  if (!excursion) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Excursion introuvable</h1>
            <Button onClick={() => navigate('/excursions')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour aux excursions
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const allImages = [excursion.cover_image, ...(excursion.images || [])].filter(Boolean) as string[];

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={`${excursion.title} - Excursion Rio de Janeiro`}
        description={excursion.resume || excursion.description_longue?.substring(0, 160) || `Découvrez ${excursion.title} avec Roca Rio. ${excursion.duree}, niveau ${excursion.niveau}/5. Réservez maintenant !`}
        canonical={`https://rocario-cleacarioca.com/excursions/${excursion.id}`}
        keywords={`${excursion.title}, excursion Rio, ${excursion.category}, guide francophone, tourisme Rio de Janeiro`}
        ogImage={excursion.cover_image || undefined}
        ogImageAlt={`${excursion.title} - Excursion à Rio de Janeiro`}
      />
      
      <TouristAttractionStructuredData
        name={excursion.title}
        description={excursion.resume || excursion.description_longue || excursion.title}
        url={`https://rocario-cleacarioca.com/excursions/${excursion.id}`}
        image={excursion.cover_image}
        price={excursion.prix_eur}
        duration={excursion.duree}
        category={excursion.category}
      />
      
      <BreadcrumbStructuredData
        items={[
          { name: 'Accueil', url: 'https://rocario-cleacarioca.com/' },
          { name: 'Excursions', url: 'https://rocario-cleacarioca.com/excursions' },
          { name: excursion.category, url: `https://rocario-cleacarioca.com/excursions?category=${excursion.category}` },
          { name: excursion.title, url: `https://rocario-cleacarioca.com/excursions/${excursion.id}` }
        ]}
      />
      
      {excursion.faq && excursion.faq.length > 0 && (
        <FAQStructuredData
          faqs={excursion.faq.map((faq: any) => ({
            question: faq.question,
            answer: faq.answer
          }))}
        />
      )}
      
      <Header />
      <main className="bg-background">
        {/* Hero Section - Grande photo de couverture */}
        <div className="relative w-full h-[50vh] sm:h-[60vh] lg:h-[70vh]">
          {isAdmin ? (
            <EditableImage
              src={excursion.cover_image || '/placeholder.svg'}
              alt={excursion.title}
              className="w-full h-full object-cover"
              bucket="site-assets"
              path={`excursions/${excursion.id}/hero-cover.jpg`}
              originalPath={`excursions/${excursion.id}/original.jpg`}
              originalSrc={(excursion as any).original_image || undefined}
              aspectRatio={16/9}
              loading="eager"
              onImageUpdate={async (newUrl) => {
                const { error } = await supabase
                  .from('excursions')
                  .update({ cover_image: newUrl })
                  .eq('id', excursion.id);
                if (!error) {
                  setExcursion(prev => prev ? { ...prev, cover_image: newUrl } : null);
                }
              }}
              onOriginalSaved={async (originalUrl) => {
                await supabase
                  .from('excursions')
                  .update({ original_image: originalUrl } as any)
                  .eq('id', excursion.id);
                setExcursion(prev => prev ? { ...prev, original_image: originalUrl } as any : null);
              }}
              onImageDelete={async () => {
                const { error } = await supabase
                  .from('excursions')
                  .update({ cover_image: null })
                  .eq('id', excursion.id);
                if (!error) {
                  setExcursion(prev => prev ? { ...prev, cover_image: null } : null);
                }
              }}
            />
          ) : (
            <img 
              src={excursion.cover_image || '/placeholder.svg'} 
              alt={excursion.title}
              className="w-full h-full object-cover"
            />
          )}
          
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent pointer-events-none" />
          
          {/* Bouton retour en haut à gauche */}
          <div className="absolute top-6 left-6 z-10">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/excursions')} 
              className="text-white hover:bg-white/20 backdrop-blur-sm"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour
            </Button>
          </div>
          
          {/* Contenu en bas */}
          <div className="absolute bottom-0 left-0 right-0 p-6 pb-8 pointer-events-none">
            <div className="container mx-auto max-w-7xl">
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge variant="secondary" className="text-sm">
                  {excursion.category}
                </Badge>
                {excursion.tags?.coup_de_coeur && (
                  <Badge className="bg-roca-yellow text-white text-sm">
                    ❤️ Coup de cœur
                  </Badge>
                )}
                {excursion.tags?.impact_solidaire && (
                  <Badge className="bg-roca-red text-white text-sm">
                    👫 Impact solidaire
                  </Badge>
                )}
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-playfair font-bold text-white mb-3 leading-tight drop-shadow-2xl">
                {excursion.title}
              </h1>
              {excursion.resume && (
                <p className="text-base sm:text-lg md:text-xl font-poppins text-white/95 max-w-3xl leading-relaxed">
                  {excursion.resume}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="container mx-auto px-4 py-8">
          {/* Breadcrumbs */}
          {/* Breadcrumbs - truncated on mobile */}
          <Breadcrumb className="mb-6">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Accueil</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/excursions">Excursions</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="hidden sm:block" />
              <BreadcrumbItem className="hidden sm:block">
                <BreadcrumbLink asChild>
                  <Link to={`/excursions?category=${excursion.category}`}>{excursion.category}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="truncate max-w-[200px] sm:max-w-none">{excursion.title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Mobile price summary - visible only on mobile, before main content */}
          <div className="lg:hidden mb-8 bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg p-4 border shadow-card">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-2xl font-playfair font-bold text-primary">{excursion.prix_eur}€</div>
                <div className="text-xs text-muted-foreground font-poppins">par personne</div>
              </div>
              <BookingButton 
                excursionTitle={excursion.title}
                whatsappCatalogId={excursion.whatsapp_catalog_id}
                className="flex-1 max-w-[200px] font-poppins"
              />
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Points forts */}
              {excursion.points_forts && excursion.points_forts.length > 0 && (
                <section>
                  <div className="w-10 h-1 bg-roca-yellow mb-4" />
                  <h2 className="text-3xl font-playfair font-bold mb-8">
                    Points forts
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {excursion.points_forts.map((point, index) => (
                      <div key={index} className="flex items-start gap-4 p-4">
                        <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 mt-0.5 font-bold text-sm">
                          {index + 1}
                        </div>
                        <span className="font-poppins leading-relaxed text-foreground">{point}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Description */}
              <section>
                <div className="w-10 h-1 bg-roca-orange mb-4" />
                <h2 className="text-3xl font-playfair font-bold mb-8">Description</h2>
                <div className="prose prose-lg max-w-none space-y-6">
                  {excursion.description_longue ? (
                    excursion.description_longue.includes('<') ? (
                      <SecureContentRenderer content={excursion.description_longue} className="[&>p:first-of-type]:first-letter:text-5xl [&>p:first-of-type]:first-letter:font-playfair [&>p:first-of-type]:first-letter:text-primary [&>p:first-of-type]:first-letter:float-left [&>p:first-of-type]:first-letter:mr-2 [&>p:first-of-type]:first-letter:leading-none" />
                    ) : (
                      excursion.description_longue.split('\n\n').map((paragraph, index) => (
                        <p key={index} className={`text-muted-foreground leading-relaxed text-base ${index === 0 ? 'first-letter:text-5xl first-letter:font-playfair first-letter:text-primary first-letter:float-left first-letter:mr-2 first-letter:leading-none' : ''}`}>
                          {paragraph}
                        </p>
                      ))
                    )
                  ) : (
                    <p className="text-muted-foreground leading-relaxed">
                      Description non disponible
                    </p>
                  )}
                </div>
                {/* Séparateur décoratif */}
                <div className="flex items-center gap-4 my-12">
                  <div className="flex-1 h-px bg-border" />
                  <div className="w-2 h-2 rounded-full bg-primary" />
                  <div className="flex-1 h-px bg-border" />
                </div>
              </section>

              {/* Itinéraire */}
              {excursion.itineraire && excursion.itineraire.length > 0 && (
                <ExcursionItinerary itineraire={excursion.itineraire} />
              )}

              {/* Pourquoi choisir */}
              {excursion.pourquoi_choisir && excursion.pourquoi_choisir.length > 0 && (
                <section>
                  <h3 className="text-2xl font-playfair font-bold mb-4">Pourquoi choisir cette excursion ?</h3>
                  <ul className="space-y-3">
                    {excursion.pourquoi_choisir.map((reason, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground leading-relaxed">{reason}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Inclusions */}
              <ExcursionInclusions
                inclus={excursion.inclus}
                nonInclus={excursion.non_inclus}
                equipement={excursion.equipement_recommande}
              />

              {/* Bon à savoir */}
              {excursion.bon_a_savoir && (
                <section>
                  <div className="flex gap-5 items-start rounded-xl p-6 bg-muted/50">
                    <div className="w-1 min-h-full bg-roca-orange rounded-full flex-shrink-0 self-stretch" />
                    <div>
                      <h3 className="text-xl font-poppins font-bold mb-3 text-foreground">
                        Bon à savoir
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">{excursion.bon_a_savoir}</p>
                    </div>
                  </div>
                </section>
              )}

              {/* FAQ */}
              {excursion.faq && excursion.faq.length > 0 && (
                <ExcursionFAQ faq={excursion.faq} />
              )}

              {/* Galerie photos - sans lightbox */}
              {excursion.images && excursion.images.length > 0 && (
                <section>
                  <h3 className="text-2xl font-playfair font-bold mb-4">Photos de l'excursion</h3>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {excursion.images.slice(0, 9).map((image, index) => (
                      <div
                        key={index}
                        className="relative aspect-square rounded-lg overflow-hidden"
                      >
                        {isAdmin ? (
                          <EditableImage
                            src={image}
                            alt={excursion.image_captions?.[index + 1] || `${excursion.title} - Photo ${index + 1}`}
                            className="w-full h-full object-cover"
                            bucket="site-assets"
                            path={`excursions/${excursion.id}/gallery-${index}.jpg`}
                            aspectRatio={1}
                            onImageUpdate={async (newUrl) => {
                              const newImages = [...(excursion.images || [])];
                              newImages[index] = newUrl;
                              const { error } = await supabase
                                .from('excursions')
                                .update({ images: newImages })
                                .eq('id', excursion.id);
                              if (!error) {
                                setExcursion(prev => prev ? { ...prev, images: newImages } : null);
                              }
                            }}
                            onImageDelete={async () => {
                              const newImages = (excursion.images || []).filter((_, i) => i !== index);
                              const { error } = await supabase
                                .from('excursions')
                                .update({ images: newImages })
                                .eq('id', excursion.id);
                              if (!error) {
                                setExcursion(prev => prev ? { ...prev, images: newImages } : null);
                              }
                            }}
                          />
                        ) : (
                          <img
                            src={image}
                            alt={excursion.image_captions?.[index + 1] || `${excursion.title} - Photo ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                    ))}
                    
                    {isAdmin && (
                      <EditableImage
                        src=""
                        alt="Ajouter une photo"
                        className="aspect-square rounded-lg"
                        bucket="site-assets"
                        allowDelete={false}
                        onImageUpdate={async (newUrl) => {
                          const newImages = [...(excursion.images || []), newUrl];
                          const { error } = await supabase
                            .from('excursions')
                            .update({ images: newImages })
                            .eq('id', excursion.id);
                          
                          if (!error) {
                            setExcursion(prev => prev ? { ...prev, images: newImages } : null);
                            toast({
                              title: "Photo ajoutée",
                              description: "La photo a été ajoutée à la galerie"
                            });
                          }
                        }}
                      />
                    )}
                  </div>
                </section>
              )}
            </div>

            {/* Sidebar Sticky */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Infos pratiques */}
                <ExcursionPracticalInfoEditor
                  excursionId={excursion.id}
                  info={{
                    debut: excursion.debut,
                    duree: excursion.duree,
                    participants_min: excursion.participants_min,
                    participants_max: excursion.participants_max,
                    niveau: excursion.niveau
                  }}
                  isAdmin={isAdmin}
                  onUpdate={(updates) => setExcursion(prev => prev ? { ...prev, ...updates } : null)}
                />

                {/* Prix et réservation */}
                <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg p-6 border shadow-card">
                  <div className="text-center mb-6">
                    <div className="text-4xl font-playfair font-bold text-primary mb-2">
                      {excursion.prix_eur}€
                    </div>
                    <div className="text-sm text-muted-foreground font-poppins">par personne</div>
                  </div>
                  <BookingButton 
                    excursionTitle={excursion.title}
                    whatsappCatalogId={excursion.whatsapp_catalog_id}
                    className="w-full font-poppins"
                  />
                  <p className="text-xs text-center text-muted-foreground mt-4">
                    Réponse sous 24h • Paiement sécurisé
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>


          {/* Sticky CTA Mobile */}
          {showStickyCTA && (
            <div className="fixed bottom-0 left-0 right-0 lg:hidden bg-background border-t shadow-float p-4 z-40 animate-fade-in safe-area-bottom">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="font-playfair font-bold text-primary text-xl">{excursion.prix_eur}€</div>
                  <div className="text-xs text-muted-foreground font-poppins">par personne</div>
                </div>
                <BookingButton 
                  excursionTitle={excursion.title}
                  whatsappCatalogId={excursion.whatsapp_catalog_id}
                  className="flex-1 font-poppins"
                />
              </div>
            </div>
          )}
      </main>
      <Footer />
      
      {/* Lightbox */}
      <ExcursionLightbox
        images={allImages}
        captions={excursion.image_captions}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
      
      <AdminToggle />
    </div>
  );
};

export default ExcursionDetail;
